import { Box, Icon, Text } from '@factorialco/deprecated-design-system'
import { F0Alert, F0Button } from '@factorialco/f0-react'
import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { CheckDouble, Cross } from '@factorialco/f0-react/icons/app'
import {
  ChangeBillableRateDocument,
  ChangeBillableRateMutation,
  ProjectManagementProjectBillableRateTypeEnum,
  ProjectManagementProjectDetailsPageDocument,
  ProjectPeopleDataCollectionDocument,
  ProjectRatesDocument,
} from 'generated/resources/graphql'

import LabeledInput from 'modules/core/components/LabeledInput'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import FlatBillRate from './FlatBillRate'
import JobBasedRates from './JobBasedRates'
import { BillingRateValues } from './types'

interface RatesSettingsSidepanelProps {
  projectId: string
  isOpen: boolean
  isJobCatalogTreeEnabled: boolean
  onClose: () => void
  levelRatesMissing: boolean
}

const RatesSettingsSidepanel = ({
  isOpen,
  onClose,
  projectId,
  isJobCatalogTreeEnabled,
  levelRatesMissing,
}: RatesSettingsSidepanelProps) => {
  const { data: projectRates } = useQuery(ProjectRatesDocument, {
    variables: { projectId },
  })

  const [changeBillableRate, { loading: runningQuery }] = useMutation(ChangeBillableRateDocument, {
    extractorFn: (data: ChangeBillableRateMutation) =>
      data.projectManagementMutations.changeBillableRateProjectmanagementProject,
    refetchQueries: [
      ProjectRatesDocument,
      ProjectPeopleDataCollectionDocument,
      ProjectManagementProjectDetailsPageDocument,
    ],
    awaitRefetchQueries: true,
  })

  const selectableOptions: Array<{
    label: string
    value: ProjectManagementProjectBillableRateTypeEnum | null
    description: string
  }> = [
    {
      label: '⠀',
      value: null,
      description: '',
    },
    {
      label: i18n.t('project_management.forms.billing_rate.options.individual.title'),
      value: 'individual',
      description: i18n.t('project_management.forms.billing_rate.options.individual.description'),
    },
    {
      label: i18n.t('project_management.forms.billing_rate.options.flat_bill.title'),
      value: 'flat_bill',
      description: i18n.t('project_management.forms.billing_rate.options.flat_bill.description'),
    },
    {
      label: i18n.t('project_management.forms.billing_rate.options.job_based.title'),
      value: 'job_based',
      description: i18n.t('project_management.forms.billing_rate.options.job_based.description'),
    },
  ]

  const getBillableRateTypeDisplayText = () => {
    return (
      selectableOptions.find((option) => option.value === values.billableRateType)?.label ||
      values.billableRateType
    )
  }

  const closeAndReset = () => {
    onClose()
    resetForm()
  }

  const initialBillableRateType = projectRates?.projectManagement.project?.billableRateType

  const { values, setValues, errors, handleSubmit, resetForm } = useForm<BillingRateValues>({
    defaultValues: {
      billableRateType: initialBillableRateType,
      rates:
        projectRates?.projectManagement.ratesConnection?.nodes
          .filter((rate): rate is typeof rate & { resource: NonNullable<typeof rate.resource> } => {
            return (
              ['JobCatalog::TreeLevel', 'JobCatalog::Level', 'ProjectManagement::Project'].includes(
                rate.resourceType
              ) && rate.resource !== null
            )
          })
          .map((rate) => {
            const levelName =
              'name' in rate.resource
                ? (rate.resource.name ?? '')
                : 'levelName' in rate.resource
                  ? (rate.resource.levelName ?? '')
                  : ''
            const roleName =
              'roleName' in rate.resource
                ? (rate.resource.roleName ?? '')
                : 'ancestor' in rate.resource
                  ? (rate.resource.ancestor?.name ?? '')
                  : ''

            return {
              id: rate.id,
              resourceId: rate.resource.id,
              resourceType: rate.resourceType,
              billingRateCents: rate.billingRateCents,
              chargingCostCents: rate.chargingCostCents,
              markupPercentageCents: rate.markupPercentageCents,
              currency: rate.currency,
              name: [roleName, levelName].filter(Boolean).join(' '),
            }
          }) ?? [],
    },
    enableReinitialize: true,
    validate: (data) => {
      const errorValidation: {
        billableRateType?: string
        rates?: Record<string | number, Record<string, string>>
      } = {}

      if (!data.billableRateType) {
        errorValidation.billableRateType = i18n.t('common.required')
      }

      data.rates.forEach((rate) => {
        const rateErrors: Record<string, string> = {}

        if (rate.billingRateCents === undefined) {
          rateErrors.billingRateCents = i18n.t(
            'project_management.forms.billing_rate.error.bill_rate_required'
          )
        }

        if (rate.chargingCostCents === undefined) {
          rateErrors.chargingCostCents = i18n.t(
            'project_management.forms.billing_rate.error.charging_cost_required'
          )
        }

        if (rate.markupPercentageCents === undefined) {
          rateErrors.markupPercentageCents = i18n.t(
            'project_management.forms.billing_rate.error.markup_percentage_required'
          )
        }

        if (Object.keys(rateErrors).length > 0) {
          if (!errorValidation.rates) {
            errorValidation.rates = {}
          }
          errorValidation.rates[rate.resourceId] = rateErrors
        }
      })

      return errorValidation
    },
    onSubmit: async (data, { setErrors }) => {
      track(trackingEvents.projectManagement.jobsAndRates.clickOnProjectConfigurationRate, {
        billableRateType: data.billableRateType,
      })

      if (!data.billableRateType) {
        return { success: false, errors: [] }
      }

      const result = await changeBillableRate({
        variables: {
          id: projectId,
          billableRateType: data.billableRateType,
          rates: data.rates.map(
            ({
              name: _name,
              chargingCostCents: _chargingCostCents,
              id: _id,
              roleName: _roleName,
              ...rate
            }) => ({
              ...rate,
            })
          ),
        },
      })
      const onSubmitError = () => {
        setErrors(errors)
      }
      const handled = handleResult(result, (err) => {
        if (err.rates) errors.rates = i18n.t('common.error')
        onSubmitError()
      })
      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('project_management.forms.billing_rate.alerts.success'),
          time: 10000,
        })
        closeAndReset()
      }
      return handled
    },
  })

  const isAnyBillRateInvalid = values.rates.some(
    (rate) => rate.billingRateCents <= 0 || isNaN(rate.billingRateCents)
  )

  const billableRateTypeIndividual = values.billableRateType === 'individual'
  const billableRateTypeProject = values.billableRateType === 'flat_bill'
  const billableRateTypeJobBased = values.billableRateType === 'job_based'

  const existingRateIds =
    projectRates?.projectManagement.ratesConnection?.nodes
      .filter(
        (rate) =>
          rate.resourceType === 'JobCatalog::Level' || rate.resourceType === 'JobCatalog::TreeLevel'
      )
      .map((rate) => rate.resource?.id)
      .filter((id): id is string => id !== null && id !== undefined) ?? []

  const defaultJobLevelIds =
    projectRates?.projectManagement.project?.projectWorkers
      ?.map((worker) => {
        const referenceContract = worker.employee?.referenceContract
        return referenceContract?.jobCatalogLevel?.id || referenceContract?.jobCatalogTreeLevel?.id
      })
      .filter(
        (id): id is string => id !== null && id !== undefined && !existingRateIds.includes(id)
      ) ?? []

  return (
    <Sidepanel
      open={isOpen}
      onClose={closeAndReset}
      title={i18n.t('project_management.forms.billing_rate.header.title')}
      width="wide"
      boxPadding={false}
      footer={
        <Box gap="s8" flexDirection="row" width="full" justifyContent="flexEnd">
          <F0Button
            label={i18n.t('common.cancel')}
            onClick={closeAndReset}
            variant="critical"
            icon={Cross}
          />
          <F0Button
            label={i18n.t('common.save')}
            onClick={handleSubmit}
            variant="default"
            icon={CheckDouble}
            disabled={isAnyBillRateInvalid}
            loading={runningQuery}
          />
        </Box>
      }
    >
      <ResourceHeader
        title={i18n.t('project_management.forms.billing_rate.title')}
        description={i18n.t('project_management.forms.billing_rate.description')}
      />
      <Box gap="s16" paddingTop="s8" paddingX="s24">
        <PopoverMenu type="list">
          <PopoverMenu.Trigger>
            <LabeledInput
              isLegend
              label={i18n.t(
                'project_management.jobs_and_rates.sidepanels.form.configuration_by.label'
              )}
              error={errors.billableRateType}
            >
              <Box
                gap="s8"
                flexDirection="row"
                justifyContent="spaceBetween"
                alignItems="center"
                border={{
                  all: {
                    width: 's2',
                    style: 'solid',
                    color: 'grey400',
                  },
                }}
                borderRadius={{
                  all: 'abs008',
                }}
                padding="s8"
                paddingX="s16"
                cursor="pointer"
              >
                <Text.Normal ellipsis>{getBillableRateTypeDisplayText()}</Text.Normal>
                <Icon.Small icon="caret-down" color="grey600" />
              </Box>
            </LabeledInput>
          </PopoverMenu.Trigger>
          <PopoverMenu.Content side="bottom" align="center" width="s400">
            <PopoverMenu.TouchableOption
              title={i18n.t('project_management.forms.billing_rate.options.individual.title')}
              description={i18n.t(
                'project_management.forms.billing_rate.options.individual.description'
              )}
              onClick={() => setValues({ ...values, billableRateType: 'individual', rates: [] })}
            />
            <PopoverMenu.TouchableOption
              title={i18n.t('project_management.forms.billing_rate.options.flat_bill.title')}
              description={i18n.t(
                'project_management.forms.billing_rate.options.flat_bill.description'
              )}
              onClick={() => {
                setValues({
                  ...values,
                  billableRateType: 'flat_bill',
                  rates: [
                    {
                      resourceId: projectId,
                      resourceType: 'ProjectManagement::Project',
                      billingRateCents: 0,
                      chargingCostCents: 0,
                      markupPercentageCents: 0,
                      currency:
                        projectRates?.projectManagement.project?.legalEntity?.currency ?? 'EUR',
                      name: '',
                    },
                  ],
                })
              }}
            />
            <PopoverMenu.TouchableOption
              title={i18n.t('project_management.forms.billing_rate.options.job_based.title')}
              description={i18n.t(
                'project_management.forms.billing_rate.options.job_based.description'
              )}
              onClick={() => setValues({ ...values, billableRateType: 'job_based', rates: [] })}
            />
          </PopoverMenu.Content>
        </PopoverMenu>

        {billableRateTypeIndividual && (
          <Box paddingY="s24">
            <F0Alert
              variant="info"
              title={i18n.t('project_management.forms.billing_rate.alerts.individual_rates.title')}
              description={i18n.t(
                'project_management.forms.billing_rate.alerts.individual_rates.description'
              )}
            />
          </Box>
        )}

        {billableRateTypeJobBased && (
          <Box paddingY="s24">
            <JobBasedRates
              rates={values.rates}
              onChange={(rates) => setValues({ ...values, rates }, false)}
              isJobCatalogTreeEnabled={isJobCatalogTreeEnabled}
              defaultJobLevelIds={defaultJobLevelIds}
              levelRatesMissing={levelRatesMissing}
            />
          </Box>
        )}
        {billableRateTypeProject && values.rates[0] && (
          <FlatBillRate
            rate={values.rates[0]}
            onChange={(rates) => setValues({ ...values, rates }, false)}
            errors={errors}
          />
        )}
      </Box>
    </Sidepanel>
  )
}

export default RatesSettingsSidepanel
